import { useEffect, useRef, useState } from "react";
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { Calendar } from 'primereact/calendar';
import { InputText } from 'primereact/inputtext';
import { FilterMatchMode } from 'primereact/api';
import { Toast } from 'primereact/toast';
import ReportService from '../../services/ReportService';

const currency = new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY' });
const fmtMoney = (v) => currency.format(v ?? 0);
const fmtDate = (isoOrDate) => (isoOrDate ? new Date(isoOrDate).toLocaleDateString('tr-TR') : '');

const toLocalYMD = (d) => {
    if (!(d instanceof Date)) return null;
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
};

export default function ExpenseReportPage() {
    const [mounted, setMounted] = useState(false);
    useEffect(() => { setMounted(true); }, []);
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [dateRange, setDateRange] = useState([new Date(new Date().getFullYear(), new Date().getMonth(), 1), new Date()]);
    const [globalFilterValue, setGlobalFilterValue] = useState('');
    const [filters, setFilters] = useState({
        global: { value: null, matchMode: FilterMatchMode.CONTAINS },
    });
    const toast = useRef(null);
    const dt = useRef(null);

    const fetchReport = async () => {
        if (!dateRange || !dateRange[0] || !dateRange[1]) return;
        setLoading(true);
        try {
            const res = await ReportService.getExpenseReport(toLocalYMD(dateRange[0]), toLocalYMD(dateRange[1]));
            setData(res.data.data);
        } catch (err) {
            toast.current.show({ severity: 'error', summary: 'Hata', detail: 'Rapor alınamadı' });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchReport();
    }, []);

    const onGlobalFilterChange = (e) => {
        const value = e.target.value;
        let _filters = { ...filters };
        _filters['global'].value = value;
        setFilters(_filters);
        setGlobalFilterValue(value);
    };

    const exportCSV = () => {
        dt.current.exportCSV();
    };

    const totalExpense = data.reduce((acc, r) => acc + (r.amount || 0), 0);
    const categoryCount = new Set(data.map(r => r.categoryName)).size;

    const header = (
        <div className="flex flex-col xl:flex-row justify-between items-start xl:items-center gap-4 py-2">
            <div className="flex flex-wrap items-center gap-3 w-full xl:w-auto">
                 <div className="flex flex-col gap-1 flex-1 sm:flex-none">
                    <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Tarih Aralığı</span>
                    <Calendar value={dateRange} onChange={(e) => setDateRange(e.value)} selectionMode="range" readOnlyInput dateFormat="dd.mm.yy" className="w-full sm:w-64" showIcon inputClassName="font-bold text-slate-700 py-2" />
                 </div>
                 <Button icon="pi pi-search" label="SORGULA" onClick={fetchReport} loading={loading} className="!bg-blue-600 border-none hover:!bg-blue-700 px-6 h-[40px] rounded-xl font-black text-[11px] shadow-lg shadow-blue-600/20 text-white mt-5" />
            </div>
            <div className="flex items-center gap-3 w-full xl:w-auto mt-2 xl:mt-5">
                <span className="p-input-icon-left flex-1 xl:flex-none">
                    <i className="pi pi-search text-slate-400 pl-2" />
                    <InputText value={globalFilterValue} onChange={onGlobalFilterChange} placeholder="Açıklama veya kategori ara..." className="w-full p-5 !py-2 bg-slate-50 border-slate-200 rounded-xl font-bold text-sm h-[40px]" />
                </span>
                <Button type="button" icon="pi pi-file-excel" label="EXCEL" severity="success" onClick={exportCSV} className="!bg-emerald-600 border-none rounded-xl px-4 h-[40px] font-black text-[11px] text-white" />
            </div>
        </div>
    );

    return (
        <div className=" p-2 md:p-4  bg-[#f8fafc] min-h-screen animate-fade-in">
            <Toast ref={toast}  appendTo={mounted ? document.body : "self"} />

            {/* Elite Header */}
            <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
                <div className="flex items-center gap-6">
                    <div className="w-20 h-20 rounded-[2rem] bg-rose-600 flex items-center justify-center shadow-2xl shadow-rose-200">
                        <i className="pi pi-wallet text-white text-3xl" />
                    </div>
                    <div>
                        <h1 className="text-3xl md:text-4xl font-black text-slate-900 leading-tight tracking-tighter uppercase">Gider Raporu</h1>
                        <div className="flex items-center gap-3 mt-1">
                            <span className="w-2 h-2 rounded-full bg-rose-500 animate-pulse" />
                            <p className="text-slate-500 font-bold text-sm uppercase tracking-widest italic">Harcama ve Maliyet Analizi</p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div className="bg-rose-50 p-4 rounded-[2.5rem] border border-white/50 shadow-xl shadow-rose-500/10 flex items-center justify-between transition-all hover:scale-[1.02]">
                    <div>
                        <p className="text-[10px] font-black text-rose-600 uppercase tracking-[0.2em] mb-2">Toplam Harcama</p>
                        <h3 className="text-3xl font-black text-rose-600 tracking-tighter">{fmtMoney(totalExpense)}</h3>
                    </div>
                    <div className="w-14 h-14 rounded-2xl bg-white shadow-sm flex items-center justify-center text-rose-600">
                        <i className="pi pi-money-bill text-2xl" />
                    </div>
                </div>
                <div className="!bg-slate-900 p-4 rounded-[2.5rem] border border-slate-800 shadow-xl shadow-slate-900/20 flex items-center justify-between transition-all hover:scale-[1.02]">
                    <div>
                        <p className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] mb-2">Aktif Gider Kategorisi</p>
                        <h3 className="text-3xl font-black text-white tracking-tighter">{categoryCount} Kategori</h3>
                    </div>
                    <div className="w-14 h-14 rounded-2xl !bg-slate-800 shadow-sm flex items-center justify-center !text-blue-400">
                        <i className="pi pi-tags text-2xl" />
                    </div>
                </div>
            </div>

            <div className="bg-white shadow-2xl shadow-slate-200/50 border border-slate-200 rounded-[2.5rem] overflow-hidden">
                <div className="p-2 md:p-4">
                    <DataTable
                        ref={dt}
                        value={data}
                        loading={loading}
                        filters={filters}
                        globalFilterFields={['description', 'categoryName']}
                        header={header}
                        paginator
                        rows={15}
                        rowsPerPageOptions={[15, 30, 50]}
                        className="p-datatable-sm"
                        emptyMessage="Kayıt bulunamadı."
                        removableSort
                        rowClassName={() => "hover:bg-slate-50/80 transition-colors"}
                    >
                        <Column field="expenseDate" header="TARİH" body={(r) => (
                             <div className="flex flex-col">
                                <span className="font-black text-slate-700">{fmtDate(r.expenseDate)}</span>
                                <span className="text-[10px] text-slate-400 font-bold uppercase tracking-tighter">İşlem Günü</span>
                             </div>
                        )} sortable headerClassName="bg-slate-50 text-slate-500 font-black text-[11px] uppercase py-4 px-6" className="py-4 px-6" />

                        <Column field="categoryName" header="KATEGORİ" body={(r) => (
                             <span className="bg-slate-100 text-slate-600 px-3 py-1 rounded-full text-[11px] font-black uppercase tracking-wider">{r.categoryName}</span>
                        )} sortable headerClassName="bg-slate-50 text-slate-500 font-black text-[11px] uppercase py-4 px-6" className="py-4 px-6" />

                        <Column field="description" header="AÇIKLAMA" body={(r) => <span className="font-bold text-slate-700">{r.description}</span>} sortable headerClassName="bg-slate-50 text-slate-500 font-black text-[11px] uppercase py-4 px-6" className="py-4 px-6" />

                        <Column field="amount" header="TUTAR" body={(r) => <span className="font-black text-rose-600 bg-rose-50 px-4 py-1.5 rounded-xl">{fmtMoney(r.amount)}</span>} sortable headerClassName="bg-slate-50 text-slate-500 font-black text-[11px] uppercase py-4 px-6 text-right" className="py-4 px-6" style={{ textAlign: 'right' }} />
                    </DataTable>
                </div>
            </div>
        </div>
    );
}
